/* global console, CustomFunctions */

// =============================================================================
// MM.SETSIZE — TypeScript port faithful to the C# Excel-DNA SetSize/ExtendFormulas/Shrink.
//
// Reference: assets/Archive/AM.XLLDistributions/XLLDistributions.cs
//   - SetSize:        line 1340  (entry, antifraud, queues macro)
//   - ExtendFormulas: line 1062  (fills empty cells with nearest non-empty formula)
//   - Shrink:         line 1265  (clears cells beyond the target rectangle)
//
// Key sectors of behaviour preserved 1:1 with C#:
//   * Anti-recursion: if the cell above or to the left already contains a
//     SETSIZE call, bail out (return 0) without resizing.
//   * Extend: scan the rectangle row by row; for every empty cell, copy the
//     nearest non-empty formula from the same row (cell to the left of the
//     empty run) or from the row above (when the empty run starts at column 0).
//   * "Whole row empty" early exit: if a row is entirely empty, copy each
//     cell of the row above into ALL subsequent rows of the rectangle, and
//     return immediately.
//   * Shrink: walk col by col starting at firstCol+nbCols, stopping at the
//     first column that is fully empty; then walk row by row similarly.
//     The clear width on the row-shrink is intentionally one column wider
//     than the scan (faithful port of the C# off-by-one — see Quirk note).
//   * On exception during extend/shrink: write the error message into the
//     calling cell (overwrites the formula), like xlRef.SetValue(e.Message).
//   * Returns 0 to the calling cell on success.
//
// Office.js-specific deviations (no observable parity impact):
//   * `ExcelAsyncUtil.QueueAsMacro` → `Excel.run(...)` launched fire-and-forget
//     while the synchronous custom function returns 0 immediately. Equivalent
//     deferred-write semantics; Office.js executes the closure in the next
//     batch once the calc engine is idle.
//   * `xlcEcho false/true` not needed — Office.js batches every operation
//     between two `context.sync()` calls into a single repaint.
//   * R1C1 mode read in C# but unused → not ported (dead code in the source).
//   * `IsEmpty(target)` re-read live in C# → replaced by an in-memory cache
//     of the rectangle's formulas snapshot, updated incrementally as we copy.
//     The cache only flags "filled vs empty" so the semantic remains exact.
// =============================================================================

const SETSIZE_MARKER = "MM.SETSIZE(";

interface SheetContext {
  Excel: any;
  context: any;
  sheet: any;
}

/**
 * Parse an Office.js invocation.address into (sheetName, localAddress).
 *
 * Office passes the calling cell as a sheet-prefixed address:
 *   "Sheet1!D18"           → simple sheet name
 *   "'My Sheet'!D18"       → sheet name with spaces is single-quoted
 *
 * The local address ("D18") is what `Worksheet.getRange(...)` expects.
 * `Workbook.getRange` is NOT a real Office.js method — see the navigation
 * back through `worksheets.getItem(...)` in mmSetSize.
 */
function parseInvocationAddress(address: string): { sheetName: string; localAddress: string } {
  const m = address.match(/^(?:'((?:[^']|'')+)'|([^!]+))!(.+)$/);
  if (m) {
    // `''` inside single-quoted sheet names is the Excel escape for a single quote.
    const sheetName = (m[1] ?? m[2]).replace(/''/g, "'");
    return { sheetName, localAddress: m[3] };
  }
  return { sheetName: "", localAddress: address };
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function isEmptyCell(value: unknown): boolean {
  return value === "" || value === null || value === undefined;
}

function containsSetSize(formula: unknown): boolean {
  return String(formula ?? "")
    .toUpperCase()
    .includes(SETSIZE_MARKER);
}

/**
 * Anti-recursion check (mirrors C# `isFromSetSize` block, lines 1351-1397).
 *
 * Reads the formula of the cell immediately above and the cell immediately
 * to the left of the caller, in a single sync round-trip. Returns true if
 * either neighbour already contains a SETSIZE invocation — in which case
 * the caller must bail to prevent cascading recalc.
 */
async function isCalledFromAdjacentSetSize(
  ctx: SheetContext,
  row: number,
  col: number
): Promise<boolean> {
  const targets: any[] = [];
  if (row > 0) targets.push(ctx.sheet.getRangeByIndexes(row - 1, col, 1, 1));
  if (col > 0) targets.push(ctx.sheet.getRangeByIndexes(row, col - 1, 1, 1));
  if (targets.length === 0) return false;

  targets.forEach((r) => r.load("formulas"));
  await ctx.context.sync();

  return targets.some((r) => containsSetSize(r.formulas?.[0]?.[0]));
}

// ---------------------------------------------------------------------------
// extend (mirrors C# ExtendFormulas, line 1062)
// ---------------------------------------------------------------------------

async function extendFormulas(
  ctx: SheetContext,
  firstRow: number,
  firstCol: number,
  nbRows: number,
  nbCols: number
): Promise<void> {
  if (nbRows === 1 && nbCols === 1) return;

  // Snapshot the rectangle's formulas. We only need the "empty vs non-empty"
  // information; the snapshot is updated locally as we issue copies, and
  // Excel applies the actual relative-reference adjustment server-side.
  const block = ctx.sheet.getRangeByIndexes(firstRow, firstCol, nbRows, nbCols);
  block.load("formulas");
  await ctx.context.sync();

  const filled: boolean[][] = (block.formulas as any[][]).map((row: any[]) =>
    row.map((cell) => !isEmptyCell(cell))
  );

  // Match the C# control flow byte for byte:
  for (let i = 0; i < nbRows; i++) {
    for (let j = 0; j < nbCols; j++) {
      if (i === 0 && j === 0) continue; // caller cell

      if (filled[i][j]) continue;

      // Scan forward to the end of the empty run
      const jIni = j;
      do {
        j++;
      } while (j < nbCols && !filled[i][j]);

      // Whole row empty (jIni===0 && j===nbCols): copy the row above to ALL
      // remaining rows in one go, then return.
      if (jIni === 0 && j === nbCols) {
        for (let subCol = 0; subCol < nbCols; subCol++) {
          const src = ctx.sheet.getRangeByIndexes(firstRow + i - 1, firstCol + subCol, 1, 1);
          const dst = ctx.sheet.getRangeByIndexes(firstRow + i, firstCol + subCol, nbRows - i, 1);
          dst.copyFrom(src, ctx.Excel.RangeCopyType.formulas);
        }
        await ctx.context.sync();
        return;
      }

      // Source cell selection :
      //   * if jIni === 0: source = cell directly above the empty run
      //   * else:          source = cell to the LEFT of the empty run (same row)
      const srcRow = jIni === 0 ? firstRow + i - 1 : firstRow + i;
      const srcCol = jIni === 0 ? firstCol : firstCol + jIni - 1;

      const src = ctx.sheet.getRangeByIndexes(srcRow, srcCol, 1, 1);
      const dst = ctx.sheet.getRangeByIndexes(firstRow + i, firstCol + jIni, 1, j - jIni);
      dst.copyFrom(src, ctx.Excel.RangeCopyType.formulas);

      // Update the local "filled" mask so subsequent iterations within this
      // sync see the cells as filled. Excel still applies the real relative
      // references on its side at the next sync.
      for (let k = jIni; k < j; k++) filled[i][k] = true;
    }
  }

  await ctx.context.sync();
}

// ---------------------------------------------------------------------------
// shrink (mirrors C# Shrink)
// ---------------------------------------------------------------------------
//Shrink by a batch of 16 for round-trip efficiency
const SHRINK_PROBE_BATCH = 16;

function columnHasValue(values: any[][], colIndex: number, rowCount: number): boolean {
  for (let r = 0; r < rowCount; r++) {
    if (!isEmptyCell(values[r]?.[colIndex])) return true;
  }
  return false;
}

function rowHasValue(values: any[][], rowIndex: number, colCount: number): boolean {
  for (let c = 0; c < colCount; c++) {
    if (!isEmptyCell(values[rowIndex]?.[c])) return true;
  }
  return false;
}

async function shrink(
  ctx: SheetContext,
  firstRow: number,
  firstCol: number,
  nbRows: number,
  nbCols: number
): Promise<void> {

  // =========================
  // COLUMN SHRINK 
  // =========================

  let j = nbCols - 1;

  while (true) {
    const probeStart = j + 1;

    const range = ctx.sheet.getRangeByIndexes(
      firstRow,
      firstCol + probeStart,
      nbRows,
      SHRINK_PROBE_BATCH
    );

    range.load("values");
    await ctx.context.sync();

    const values = range.values as any[][];

    let progressed = false;

    for (let k = 0; k < SHRINK_PROBE_BATCH; k++) {
      if (columnHasValue(values, k, nbRows)) {
        j++;              // to check the columns before if we found some values
        progressed = true;
      } else {
        break;            // found empty one 
      }
    }

    if (!progressed) break;

    if (j < probeStart + SHRINK_PROBE_BATCH - 1) break;
  }

  const nbColsDeleted = j - nbCols + 1;

  if (nbColsDeleted >= 1) {
    const target = ctx.sheet.getRangeByIndexes(
      firstRow,
      firstCol + nbCols,
      nbRows,
      nbColsDeleted
    );
    target.clear(ctx.Excel.ClearApplyTo.contents);
  }

  // =========================
  // ROW SHRINK 
  // =========================

  const scanColWidth = nbCols + nbColsDeleted;
  const clearColWidth = nbCols + 1 + nbColsDeleted;

  let i = nbRows - 1;

  while (true) {
    const probeStart = i + 1;

    const range = ctx.sheet.getRangeByIndexes(
      firstRow + probeStart,
      firstCol,
      SHRINK_PROBE_BATCH,
      scanColWidth
    );

    range.load("values");
    await ctx.context.sync();

    const values = range.values as any[][];

    let progressed = false;

    for (let k = 0; k < SHRINK_PROBE_BATCH; k++) {
      if (rowHasValue(values, k, scanColWidth)) {
        i++;              
        progressed = true;
      } else {
        break;            
      }
    }

    if (!progressed) break;

    if (i < probeStart + SHRINK_PROBE_BATCH - 1) break;
  }

  if (i >= nbRows) {
    const target = ctx.sheet.getRangeByIndexes(
      firstRow + nbRows,
      firstCol,
      i - nbRows + 1,
      clearColWidth
    );

    target.clear(ctx.Excel.ClearApplyTo.contents);
  }

  await ctx.context.sync();
}

// ---------------------------------------------------------------------------
// public entry — mirrors C# MM_SETSIZE wrapper (line 1842)
// ---------------------------------------------------------------------------

/**
 * MM.SETSIZE(nbRows, nbCols)
 *
 * Faithful TypeScript port of the C# `MM_SETSIZE` Excel-DNA function.
 * See the file header comment for the full behavioural contract.
 */
export async function mmSetSize(
  nbRows: number,
  nbCols: number,
  invocation: CustomFunctions.Invocation
): Promise<number> {
  const address = invocation?.address;
  if (!address) {
    console.warn("[SETSIZE] missing invocation.address — check @requiresAddress in JSDoc");
    return 0;
  }

  const Excel = (globalThis as any).Excel;
  if (!Excel || typeof Excel.run !== "function") {
    console.warn("[SETSIZE] Excel.run unavailable on this runtime");
    return 0;
  }

  const rows = Math.max(1, Math.trunc(nbRows));
  const cols = Math.max(1, Math.trunc(nbCols));

  const { sheetName, localAddress } = parseInvocationAddress(address);

  // Resolve the worksheet hosting the caller cell. The address sent by Office
  // is sheet-prefixed (e.g. "Sheet1!D18"), so we navigate via `worksheets`
  // rather than the non-existent `workbook.getRange()`.
  const resolveCallerRange = (context: any) =>
    (sheetName
      ? context.workbook.worksheets.getItem(sheetName)
      : context.workbook.worksheets.getActiveWorksheet()
    ).getRange(localAddress);

  // Fire-and-forget. The custom function returns 0 immediately so Excel can
  // commit the formula's display value; the resize work runs in the next
  // batch — equivalent to C#'s ExcelAsyncUtil.QueueAsMacro semantics.
  Excel.run(async (context: any) => {
    const callerRange = resolveCallerRange(context);
    callerRange.load(["rowIndex", "columnIndex"]);
    await context.sync();

    const ctx: SheetContext = {
      Excel,
      context,
      sheet: callerRange.worksheet,
    };
    const row: number = callerRange.rowIndex;
    const col: number = callerRange.columnIndex;

    if (await isCalledFromAdjacentSetSize(ctx, row, col)) return;

    await extendFormulas(ctx, row, col, rows, cols);
    await shrink(ctx, row, col, rows, cols);
  }).catch(async (err: any) => {
    const message =
      (err && (err.message || err.code || err.debugInfo?.message)) || "MM.SETSIZE failed";
    console.error("[SETSIZE] resize failed", { address, nbRows: rows, nbCols: cols, err });

    // Match C# behaviour (catch in SetSize -> xlRef.SetValue(e.Message)):
    // overwrite the calling cell with the error string. This destroys the
    // formula in the cell, but matches what the XLL did when an exception
    // bubbled up from ExtendFormulas/Shrink.
    try {
      await Excel.run(async (context: any) => {
        const cell = resolveCallerRange(context);
        cell.values = [[`#MM.SETSIZE: ${message}`]];
      });
    } catch (writeErr) {
      // Reporter failed too — at this point we've already logged the original.
      console.error("[SETSIZE] failed to write error to caller cell", writeErr);
    }
  });

  return 0;
}
